// Account Sub-Accounts tab (CRM batch 2026-07-24). Sub-Accounts are ONE
// level deep: a parent Account (e.g. Bozzuto) has Sub-Accounts (each building
// like "The Alure"), and each Sub-Account is a full peer client with its own
// contacts / addresses / budgets — NO field inheritance.
//
//   • Lists sub-accounts via GET /clients?parent_client_id=<id>; each row
//     links to that client's own detail page.
//   • When THIS account is itself a Sub-Account, shows the parent Account link
//     instead of the add affordance (one level max — sub-accounts can't nest).
//   • "Add sub-account": link an existing parentless/childless account
//     (search-select, mirrors MergeDialog) OR create a brand-new one — both
//     PATCH/POST the child so its parent_client_id points at this account.
import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill, clientStatusTone } from "@/components/ui/StatusPill";
import type { ClientDetailContext } from "@/pages/ClientDetailPage";
import { useAuth } from "@/auth/AuthProvider";
import { useMentionablePeople } from "@/components/notes/MentionTextarea";

interface SubAccountRow {
  id: string;
  name: string;
  status: string | null;
  industry: string | null;
  parentClientId: string | null;
  clientRepId?: string | null;
}

interface ClientsListResponse {
  rows: SubAccountRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

// Maps the backend 400 invalid_parent_account reason to plain copy.
function parentErrorMessage(reason: string | undefined): string {
  switch (reason) {
    case "self_parent":
      return "An account can't be its own sub-account.";
    case "parent_not_found":
      return "That account no longer exists.";
    case "cycle":
      return "That would create a loop between the two accounts.";
    case "parent_has_parent":
      return "This account is already a sub-account — sub-accounts can't have their own sub-accounts (one level max).";
    case "self_has_children":
      return "That account already has its own sub-accounts, so it can't become one.";
    default:
      return "That account can't be added as a sub-account.";
  }
}

export function ClientSubAccountsTab() {
  const { client, canEdit, clientId } =
    useOutletContext<ClientDetailContext>();
  const isSubAccount = client.parentClientId !== null;
  // Unlink a sub-account linked by mistake (Stephanie 2026-10-30) — it
  // becomes a stand-alone account again; nothing else changes.
  const qc = useQueryClient();
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const unlink = useMutation({
    mutationFn: (subId: string) => api(`/clients/${subId}`, { method: "PATCH", body: JSON.stringify({ parent_client_id: null }) }),
    onSuccess: () => {
      setUnlinking(null);
      setUnlinkError(null);
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => setUnlinkError(e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? "We couldn't unlink it. Try again.") : "We couldn't unlink it. Try again."),
  });

  const subQuery = useQuery({
    queryKey: ["clients", clientId, "sub-accounts"],
    queryFn: () =>
      api<ClientsListResponse>(
        `/clients?parent_client_id=${clientId}&limit=500`,
      ),
    enabled: clientId.length > 0,
  });

  // Parent Account name (only when this account is itself a sub-account).
  const parentQuery = useQuery({
    queryKey: ["clients", client.parentClientId, "detail"],
    queryFn: () =>
      api<{ client: { id: string; name: string } }>(
        `/clients/${client.parentClientId}`,
      ),
    enabled: isSubAccount && client.parentClientId !== null,
  });

  const subAccounts = subQuery.data?.rows ?? [];
  const [isAdding, setIsAdding] = useState(false);

  return (
    <section>
      {/* Parent banner — shown when viewing a sub-account. */}
      {isSubAccount && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-brown-100 bg-cream-50 px-4 py-3 text-sm">
          <Building2 className="h-4 w-4 text-brown-300" aria-hidden />
          <span className="text-brown-400">Sub-account of</span>
          <Link
            to={`/clients/${client.parentClientId}`}
            className="font-semibold text-brown-500 underline-offset-4 hover:underline"
          >
            {parentQuery.data?.client.name ?? "parent account"}
          </Link>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brown-500">
          Sub-Accounts{" "}
          <span className="text-brown-300">({subAccounts.length})</span>
        </h2>
        {/* Only a top-level Account (not itself a sub-account) can add
            sub-accounts — one level max. */}
        {canEdit && !isSubAccount && !isAdding && (
          <Button size="sm" variant="outline" onClick={() => setIsAdding(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add sub-account
          </Button>
        )}
      </div>

      {isSubAccount && (
        <p className="mb-4 text-sm text-brown-300">
          This account is a sub-account, so it can't have its own sub-accounts
          (sub-accounts are one level deep).
        </p>
      )}

      {isAdding && !isSubAccount && (
        <div className="mb-6">
          <AddSubAccountPanel
            parentId={clientId}
            parentRepId={client.clientRepId ?? null}
            onClose={() => setIsAdding(false)}
          />
        </div>
      )}

      {subQuery.isLoading ? (
        <div className="text-sm text-brown-300">Loading sub-accounts…</div>
      ) : subAccounts.length === 0 ? (
        !isSubAccount && (
          <div className="rounded-lg border border-dashed border-brown-100 px-6 py-12 text-center">
            <p className="text-sm text-brown-400">
              No sub-accounts yet.
              {canEdit &&
                " Add a building, location, or division that rolls up to this account."}
            </p>
          </div>
        )
      ) : (
        <div className="overflow-hidden rounded-lg border border-brown-100 bg-white">
          <table className="w-full">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Industry</th>
                <th className="px-6 py-3">Status</th>
                {canEdit && <th className="px-6 py-3"><span className="sr-only">Unlink</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {subAccounts.map((s) => (
                <tr
                  key={s.id}
                  className="group transition-colors hover:bg-cream-50"
                >
                  <td className="px-6 py-4">
                    <Link
                      to={`/clients/${s.id}`}
                      className="font-semibold text-brown-500 underline-offset-4 group-hover:underline"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-brown-400">
                    {s.industry ?? "—"}
                  </td>
                  <td className="px-6 py-4">
                    <StatusPill tone={clientStatusTone(s.status)}>
                      {s.status ?? "unknown"}
                    </StatusPill>
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4 text-right text-sm">
                      {unlinking === s.id ? (
                        <span className="inline-flex flex-wrap items-center justify-end gap-2">
                          <span className="text-brown-500">Unlink {s.name} from {client.name}?</span>
                          <button type="button" disabled={unlink.isPending} onClick={() => unlink.mutate(s.id)} className="font-semibold text-rust-600 hover:underline">
                            Yes, unlink
                          </button>
                          <button type="button" onClick={() => setUnlinking(null)} className="text-muted hover:text-ink">No</button>
                        </span>
                      ) : (
                        <button type="button" onClick={() => { setUnlinkError(null); setUnlinking(s.id); }} className="font-medium text-muted hover:text-ink">
                          Unlink
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {unlinkError && <p className="border-t border-brown-50 px-6 py-3 text-sm text-rust-600">{unlinkError}</p>}
        </div>
      )}
    </section>
  );
}

// ─── Add sub-account panel: link existing OR create new ──────────────────

function AddSubAccountPanel({
  parentId,
  parentRepId,
  onClose,
}: {
  parentId: string;
  parentRepId: string | null;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"link" | "create">("link");
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          {(["link", "create"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                mode === m
                  ? "bg-brown-500 text-cream-50"
                  : "bg-cream-100 text-brown-400 hover:bg-cream-200"
              }`}
            >
              {m === "link" ? "Link existing" : "Create new"}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-brown-300 hover:text-brown-500"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {mode === "link" ? (
        <LinkExistingSubAccount parentId={parentId} parentRepId={parentRepId} onDone={onClose} />
      ) : (
        <CreateSubAccount parentId={parentId} onDone={onClose} />
      )}
    </div>
  );
}

function LinkExistingSubAccount({
  parentId,
  parentRepId,
  onDone,
}: {
  parentId: string;
  parentRepId: string | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canChangeRep = !!user && (user.roles.includes("admin") || user.roles.includes("finance"));
  const people = useMentionablePeople().data?.people ?? [];
  const nameOf = (id: string | null | undefined) => people.find((p) => p.id === id)?.name ?? "the current rep";
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<SubAccountRow | null>(null);

  const search = useQuery({
    queryKey: ["clients", "sub-account-search", searchTerm],
    queryFn: () =>
      api<ClientsListResponse>(
        `/clients?search=${encodeURIComponent(searchTerm.trim())}&limit=10`,
      ),
    enabled: searchTerm.trim().length >= 2,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error("no account selected");
      return api(`/clients/${selected.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_client_id: parentId }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onDone();
    },
  });

  let errorMsg: string | null = null;
  if (mutation.error instanceof ApiError) {
    if (mutation.error.status === 400) {
      const body = (mutation.error.body ?? {}) as { reason?: string };
      errorMsg = parentErrorMessage(body.reason);
    } else {
      errorMsg = `Couldn't link account (${mutation.error.status})`;
    }
  } else if (mutation.error) {
    errorMsg = "Couldn't link account — see console";
  }

  // Candidate list excludes this parent + any account already parented here.
  const candidates =
    search.data?.rows.filter(
      (r) => r.id !== parentId && r.parentClientId !== parentId,
    ) ?? [];

  return (
    <div>
      <p className="text-sm text-brown-400">
        Pick an existing account to roll up under this one. It keeps its own
        contacts, addresses, and budgets — nothing is inherited.
      </p>
      <label className="mt-4 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        Search account
      </label>
      <Input
        className="mt-2"
        placeholder="Type at least 2 characters…"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setSelected(null);
        }}
      />
      {searchTerm.trim().length >= 2 && (
        <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-brown-100">
          {search.isLoading ? (
            <p className="px-3 py-2 text-xs text-brown-300">Searching…</p>
          ) : candidates.length === 0 ? (
            <p className="px-3 py-2 text-xs text-brown-300">No matches.</p>
          ) : (
            candidates.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelected(r)}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                  selected?.id === r.id
                    ? "bg-peach-100 text-brown-500"
                    : "text-brown-400 hover:bg-cream-100"
                }`}
              >
                <span className="font-medium">{r.name}</span>
                <StatusPill tone={clientStatusTone(r.status)}>
                  {r.status ?? "unknown"}
                </StatusPill>
              </button>
            ))
          )}
        </div>
      )}
      {/* The main account's rep comes with it (Stephanie 2026-10-22). */}
      {selected && parentRepId && selected.clientRepId !== parentRepId && (
        <p className="mt-4 rounded-md border border-brown-100 bg-cream-100 px-3 py-2 text-sm text-brown-500">
          {canChangeRep
            ? `Its rep changes from ${nameOf(selected.clientRepId)} to ${nameOf(parentRepId)}, the main account's rep. You can change it on the account afterwards.`
            : `Its rep stays ${nameOf(selected.clientRepId)}. Only admin or finance can change the rep — ask them to switch it to ${nameOf(parentRepId)}.`}
        </p>
      )}
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={!selected || mutation.isPending}
        >
          {mutation.isPending
            ? "Linking…"
            : selected
              ? `Link ${selected.name}`
              : "Pick an account"}
        </Button>
      </div>
    </div>
  );
}

function CreateSubAccount({
  parentId,
  onDone,
}: {
  parentId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [status, setStatus] = useState<"prospect" | "active">("active");

  const mutation = useMutation({
    mutationFn: () =>
      api<{ client: { id: string } }>("/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          status,
          parent_client_id: parentId,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onDone();
    },
  });

  let errorMsg: string | null = null;
  if (mutation.error instanceof ApiError) {
    if (mutation.error.status === 400) {
      const body = (mutation.error.body ?? {}) as { reason?: string };
      errorMsg = parentErrorMessage(body.reason);
    } else {
      errorMsg = `Couldn't create sub-account (${mutation.error.status})`;
    }
  } else if (mutation.error) {
    errorMsg = "Couldn't create sub-account — see console";
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim().length === 0) return;
        mutation.mutate();
      }}
    >
      <p className="text-sm text-brown-400">
        Create a new account that rolls up under this one (e.g. a building or
        location). It starts empty with its own contacts and budgets.
      </p>
      <label
        htmlFor="new-sub-account-name"
        className="mt-4 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
      >
        Account name
      </label>
      <Input
        id="new-sub-account-name"
        autoFocus
        className="mt-2"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={200}
        placeholder="e.g. The Alure"
      />
      <div className="mt-4">
        <span className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Status
        </span>
        <div className="mt-2 flex gap-2">
          {(["prospect", "active"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                status === s
                  ? "bg-brown-500 text-cream-50"
                  : "bg-cream-100 text-brown-400 hover:bg-cream-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={name.trim().length === 0 || mutation.isPending}
        >
          {mutation.isPending ? "Creating…" : "Create sub-account"}
        </Button>
      </div>
    </form>
  );
}
